import { Controller } from '@hotwired/stimulus';
import heartsService from '../hearts/service';
import { scheduleAfterPaint } from '../schedule-background.js';

const randomFloat = (min, max) => Math.random() * (max - min) + min;

export default class extends Controller {
  static targets = ['value'];

  static values = {
    id: String,
    update: { type: Boolean, default: true },
    transitionSeconds: { type: Number, default: 7 }
  };

  connect() {
    this.connected = true;
    this.connection = Symbol();
    this.started = false;
    this.animations = new Map();
    this.count = null;
    this.handleMessage = this.handleMessage.bind(this);

    this.element.classList.add('heart--loading');

    const schedule = () => {
      this.cancelStart = scheduleAfterPaint(() => this.start());
    };
    if (typeof window.IntersectionObserver === 'function') {
      const observer = new window.IntersectionObserver((entries) => {
        if (this.visibilityObserver !== observer || !entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        this.visibilityObserver = null;
        schedule();
      }, { rootMargin: '200px' });
      this.visibilityObserver = observer;
      observer.observe(this.element);
    } else {
      schedule();
    }
  }

  start() {
    if (!this.connected || this.started) return;
    this.started = true;
    this.cancelStart?.();
    this.cancelStart = null;
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    this.unsubscribe = heartsService.subscribe(this.idValue, this.handleMessage);

    this.refresh().catch((error) => {
      console.error('Failed to load hearts', error);
    });
  }

  disconnect() {
    this.connected = false;
    this.cancelStart?.();
    this.cancelStart = null;
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    this.clearAnimations();
  }

  async refresh() {
    const connection = this.connection;
    const count = await heartsService.fetchCount(this.idValue);
    if (this.connected && this.connection === connection) this.setCount(Math.max(this.count ?? 0, count));
  }

  async add(event) {
    event.preventDefault();
    this.start();
    const connection = this.connection;

    if (!this.updateValue) {
      this.setCount((this.count ?? 0) + 1);
      return;
    }

    try {
      const confirmedCount = await heartsService.increment(this.idValue);
      // A failed save leaves the last confirmed count visible. A WebSocket
      // update may already have delivered this response (or a newer count).
      if (this.connected && this.connection === connection) this.setCount(Math.max(confirmedCount, this.count ?? 0));
    } catch (error) {
      console.error('Failed to increment hearts', error);
    }
  }

  handleMessage(event) {
    let payload;

    try {
      payload = JSON.parse(event.data);
    } catch (error) {
      console.warn('Failed to parse hearts event', error);
      return;
    }

    if (payload.method !== 'UPDATED' || payload.id !== this.idValue) {
      return;
    }

    const value = Number.parseInt(payload.value, 10);
    if (Number.isNaN(value)) {
      return;
    }

    this.setCount(value);
  }

  setCount(count) {
    if (!Number.isSafeInteger(count) || count < 0) {
      return;
    }

    const previous = this.count;
    this.count = count;

    if (this.hasValueTarget) {
      this.valueTarget.textContent = count;
    }

    this.element.classList.remove('heart--loading');

    if (previous === null || previous === undefined) {
      return;
    }

    const diff = count - previous;
    if (diff <= 0) {
      return;
    }

    for (let index = 0; index < diff; index += 1) {
      this.createAnimation();
    }
  }

  createAnimation() {
    const animationBox = document.createElement('div');
    animationBox.className = 'heart-animation-box';
    animationBox.style.setProperty(
      '--heart-animated-duration',
      `${randomFloat(5, 15).toPrecision(2)}s`
    );
    animationBox.style.setProperty('--heart-box-left', `${randomFloat(-4, 4)}rem`);
    animationBox.style.setProperty('--heart-box-height', `${randomFloat(10, 20)}rem`);
    animationBox.style.setProperty(
      '--heart-box-rotation',
      randomFloat(-1, 1) > 0 ? '180deg' : '0'
    );

    const heartSymbol = document.createElement('div');
    heartSymbol.className = 'heart-symbol heart-animated';
    animationBox.appendChild(heartSymbol);

    this.element.appendChild(animationBox);

    const timeoutId = window.setTimeout(() => {
      animationBox.remove();
      this.animations.delete(timeoutId);
    }, this.transitionSecondsValue * 1000);

    this.animations.set(timeoutId, { node: animationBox, timeoutId });
  }

  clearAnimations() {
    for (const { node, timeoutId } of this.animations.values()) {
      clearTimeout(timeoutId);
      if (node && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    }

    this.animations.clear();
  }
}
